import React from 'react';
import { PlaneIcon, ShipIcon, TrailerIcon } from '../../components/ui/Icons';
import { MODE_LABELS, ShipmentMode } from '../../shipments/types';
import styles from './clientModes.module.css';

/**
 * Modo del cliente: terrestre, marítimo y/o aéreo.
 *
 * ## Por qué vive en su propio archivo
 *
 * 🔑 Se usa en **tres** lugares —el alta desde la lista de clientes, la edición desde el detalle y la
 * insignia de la tarjeta—, y las tres tienen que coincidir en el orden y en las etiquetas. Con el
 * selector copiado, agregar un modo o cambiar un nombre deja dos pantallas diciendo cosas distintas
 * sobre el mismo cliente.
 *
 * 📌 Las etiquetas y los iconos **se reusan de `shipments/types`** en lugar de escribirse aquí. Es lo
 * que hace que un cliente marcado como "Ocean" se lea igual en su ficha que en el módulo marítimo; con
 * etiquetas propias, "Ocean" y "Maritime" acabarían conviviendo.
 */

/** Las tres banderas, tal como las guarda el backend. */
export interface ClientModes {
  mode_land: boolean;
  mode_ocean: boolean;
  mode_air: boolean;
}

/**
 * Los tres encendidos.
 *
 * ⚠️ **Ya NO es el valor de partida del alta.** Se conserva porque sigue siendo útil —lo usa el estado
 * inicial del modal de edición, que enseguida se sobrescribe con los modos reales del cliente— pero para
 * crear se arranca con `NO_MODES_SELECTED`. Ver la nota de ahí abajo.
 */
export const ALL_MODES_ON: ClientModes = {
  mode_land: true,
  mode_ocean: true,
  mode_air: true,
};

/**
 * Con qué modo arranca el formulario de alta: **con ninguno marcado**.
 *
 * 🔑 Aclaración del usuario, y corrige lo que se había hecho: *"el default es para los clientes actuales;
 * los que vengan de ahora en adelante no es necesariamente un terrestre o algún otro tipo por default"*.
 *
 * Los 206 que ya existían **son** terrestres y se corrigieron con un UPDATE. Pero eso es un hecho sobre
 * los datos de hoy, **no una regla para los que vienen**: preseleccionar terrestre convertiría una
 * observación en una suposición, y quien diera de alta un cliente aéreo sin fijarse lo guardaría como
 * terrestre. Un default equivocado se propaga en silencio; una casilla vacía se ve.
 *
 * ⚠️ Y por eso el alta **exige elegir al menos uno**. Sin default y sin exigencia, el camino fácil
 * —dejarlo como está y guardar— produce un cliente sin modo, que no se ofrece en ninguna parte y sin
 * ninguna señal de por qué. La elección se pide explícita, no se adivina.
 */
export const NO_MODES_SELECTED: ClientModes = {
  mode_land: false,
  mode_ocean: false,
  mode_air: false,
};

/** `true` si el cliente tiene al menos un modo. Lo usa el alta para exigir la elección. */
export const hasAnyMode = (modes: ClientModes): boolean =>
  MODE_FIELDS.some(({ key }) => modes[key]);

/** Orden y metadatos de los tres modos. Único lugar donde se declara. */
export const MODE_FIELDS: Array<{
  key: keyof ClientModes;
  mode: ShipmentMode;
  icon: React.ReactNode;
}> = [
  { key: 'mode_land', mode: 'land', icon: <TrailerIcon size={15} /> },
  { key: 'mode_ocean', mode: 'ocean', icon: <ShipIcon size={15} /> },
  { key: 'mode_air', mode: 'air', icon: <PlaneIcon size={15} /> },
];

/**
 * Lee una bandera de modo del API.
 *
 * 🔴 **Aquí estaba el defecto que hacía que TODOS los clientes mostraran los tres modos.**
 *
 * Se comparaba con `!== false`, y MySQL devuelve las columnas `tinyint(1)` como **números**: la lista
 * llega con `mode_ocean: 0`. Y en JavaScript **`0 !== false` es `true`**, así que un modo apagado se leía
 * como encendido. En el detalle no pasaba porque esa consulta no usa `raw: true` y Sequelize sí convierte
 * a booleano — de ahí la contradicción que se reportó: la tarjeta decía tres modos y el detalle uno.
 *
 * 📌 La comparación estricta se puso para poder distinguir "el campo no vino" de "el campo es false", y esa
 * intención era correcta; lo que estaba mal era **suponer que solo llegarían booleanos**. Ahora se separan
 * los dos casos de forma explícita: ausente → respaldo, presente → se convierte, venga como `0`, `false`,
 * `"0"` o `1`.
 *
 * ⚠️ El respaldo sigue en `true` para cuando el campo **no viene**: si el SQL de modos no corrió en ese
 * ambiente, decir que ningún cliente tiene modo sería una afirmación falsa sobre los datos, y además los
 * marcaría todos con la insignia de alerta.
 */
const flag = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  // `"0"` incluido a propósito: algunos drivers devuelven los tinyint como cadena.
  if (value === '0') return false;
  return Boolean(value);
};

/** Lee las banderas de un cliente que viene del API, con respaldo por si el campo no llega. */
export const modesOf = (client: any): ClientModes => ({
  mode_land: flag(client?.mode_land),
  mode_ocean: flag(client?.mode_ocean),
  mode_air: flag(client?.mode_air),
});

/* -------------------------------------------------------------------------- */
/*                                 Selector                                   */
/* -------------------------------------------------------------------------- */

interface ModeSelectorProps {
  value: ClientModes;
  onChange: (next: ClientModes) => void;
  disabled?: boolean;
}

/**
 * Casillas de los tres modos.
 *
 * ⚠️ **No impide dejar los tres apagados.** Se pensó en bloquear la última casilla, y se descartó: en
 * medio de una edición uno apaga los tres para volver a encender solo el que quiere, y bloquear eso
 * obliga a hacerlo en un orden concreto sin decir cuál. El aviso va abajo, y quien guarda así lo hace
 * viendo la consecuencia escrita.
 */
export const ClientModeSelector: React.FC<ModeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const none = !value.mode_land && !value.mode_ocean && !value.mode_air;

  return (
    <div className={styles.selector}>
      <div className={styles.options} role="group" aria-label="Client modes">
        {MODE_FIELDS.map(({ key, mode, icon }) => {
          const checked = value[key];
          return (
            <label
              key={key}
              className={[styles.option, checked ? styles.optionOn : '']
                .filter(Boolean)
                .join(' ')}
            >
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange({ ...value, [key]: e.target.checked })}
              />
              <span className={styles.optionIcon}>{icon}</span>
              <span className={styles.optionLabel}>{MODE_LABELS[mode]}</span>
            </label>
          );
        })}
      </div>

      {none && (
        <span className={styles.warning} role="alert">
          With no mode selected this client will not be offered in any module.
        </span>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                                 Insignia                                   */
/* -------------------------------------------------------------------------- */

/**
 * Insignia de solo lectura, para la tarjeta y el encabezado del detalle.
 *
 * 📌 Cuando están los tres se dibuja **"All modes"** en lugar de las tres pastillas. Es el caso de
 * partida de todos los clientes migrados, así que dibujarlas llenaría la lista de ruido idéntico y las
 * insignias dejarían de servir para distinguir — que es lo único que se les pide.
 */
export const ClientModeBadge: React.FC<{
  client: any;
  className?: string;
  /**
   * Solo iconos, sin texto.
   *
   * 🔴 Existe por un problema medido, no por gusto: en la lista de clientes la insignia iba dentro de la
   * pastilla del pie, junto a "Active" y al botón "View Detail". En una cuadrícula de cinco columnas esa
   * pastilla mide unos 165px y los tres elementos no caben, así que la insignia **se iba al segundo
   * renglón** y crecía el pie de todas las tarjetas — es el descuadre que se reportó.
   *
   * 📌 En modo compacto la insignia pasa al renglón del nombre, que sí tiene el ancho de la tarjeta, y
   * mide unos 24px por modo. El texto se conserva en `title` y en `aria-label`, así que no se pierde para
   * quien usa lector de pantalla ni al pasar el cursor.
   */
  compact?: boolean;
}> = ({ client, className, compact = false }) => {
  const modes = modesOf(client);
  const on = MODE_FIELDS.filter(({ key }) => modes[key]);
  const label = on.length
    ? on.map(({ mode }) => MODE_LABELS[mode]).join(' · ')
    : 'No mode assigned';

  if (compact) {
    /**
     * ⚠️ En compacto **no** hay caso especial para "los tres": se dibujan los tres iconos.
     *
     * La versión con texto lo colapsa a "All modes" porque tres pastillas con letra son ruido, pero tres
     * iconos de 15px ocupan menos que esa frase y además dicen *cuáles* son. Y con los clientes pasando a
     * terrestre, el caso de los tres deja de ser el común.
     */
    return (
      <span
        className={[styles.badgeCompact, className].filter(Boolean).join(' ')}
        title={label}
        aria-label={label}
      >
        {on.length === 0 ? (
          <span className={styles.compactNone} aria-hidden="true">
            !
          </span>
        ) : (
          on.map(({ key, mode, icon }) => (
            <span key={key} className={[styles.compactIcon, styles[`badge_${mode}`]].join(' ')}>
              {icon}
            </span>
          ))
        )}
      </span>
    );
  }

  if (on.length === 0) {
    return <span className={[styles.badgeNone, className].filter(Boolean).join(' ')}>No mode</span>;
  }
  if (on.length === MODE_FIELDS.length) {
    return <span className={[styles.badgeAll, className].filter(Boolean).join(' ')}>All modes</span>;
  }

  return (
    <span className={[styles.badges, className].filter(Boolean).join(' ')}>
      {on.map(({ key, mode, icon }) => (
        <span key={key} className={[styles.badge, styles[`badge_${mode}`]].join(' ')}>
          <span className={styles.badgeIcon}>{icon}</span>
          {MODE_LABELS[mode]}
        </span>
      ))}
    </span>
  );
};
